import * as Dialog from "@radix-ui/react-dialog";
import { CheckCircle2, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { getContrastTextColor } from "../../lib/color-utils";

type AlertType = "success" | "error" | "warning" | "info";

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: AlertType;
  primaryColor?: string;
  buttonText?: string;
}

export function AlertModal({
  isOpen,
  onClose,
  title,
  message,
  type = "info",
  primaryColor = "#0F52BA", // Default to brand sapphire
  buttonText = "OK",
}: AlertModalProps) {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return {
          icon: <CheckCircle2 className="text-slate-900" size={18} />,
          iconBg: "bg-slate-50",
          buttonBg: "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
        };
      case "error":
        return {
          icon: <AlertCircle className="text-slate-900" size={18} />,
          iconBg: "bg-slate-50",
          buttonBg: "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="text-amber-600" size={18} />,
          iconBg: "bg-amber-50/50",
          buttonBg: "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
        };
      case "info":
      default:
        return {
          icon: <Info className="text-slate-900" size={18} />,
          iconBg: "bg-slate-50",
          buttonBg: "bg-slate-900 hover:bg-black focus-visible:ring-slate-900",
          customBg: true,
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-[2px] transition-opacity" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[100] translate-x-[-50%] translate-y-[-50%] bg-white rounded-xl shadow-xl border border-slate-100 w-[calc(100%-2rem)] sm:w-full sm:max-w-sm transform transition-all outline-none animate-in fade-in zoom-in-95 duration-200 overflow-hidden flex flex-col items-start text-left">
          <div className="p-6">
            <div
              className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${styles.iconBg} border border-slate-100`}
            >
              {styles.icon}
            </div>

            <Dialog.Title className="text-base font-bold text-slate-900 tracking-tight leading-tight mb-2">
              {title}
            </Dialog.Title>

            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed mb-2">
              Notice
            </p>

            <Dialog.Description className="text-slate-600 text-sm leading-relaxed">
              {message}
            </Dialog.Description>
          </div>

          <div className="w-full px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className={`w-full sm:w-auto px-6 py-2 rounded-lg font-bold text-white transition-all focus-visible:outline-none focus-visible:ring-2 active:scale-[0.98] ${
                styles.customBg ? "" : styles.buttonBg
              }`}
              style={
                styles.customBg
                  ? {
                      backgroundColor: primaryColor,
                      color: getContrastTextColor(primaryColor),
                    }
                  : undefined
              }
            >
              {buttonText}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
